import axios from 'axios';

export type AnswerStatus = 'Pending' | 'Reconciled';

export interface AnswerItem {
  questionText?: string;
  questionId?: number;
  answer: {
    text: string;
    audio?: string;
    video?: string;
    thumbnail?: string;
  };
}

export interface AnswerDocument {
  applicationGuid: string;
  answers: AnswerItem[];
  status: AnswerStatus;
  createdAt?: string;
}

/**
 * Fetch the upstream answers document for an applicant. The backend proxies
 * GET /api/v1/answer/:applicantGuid to the Job Application API. Returns null
 * when no document exists (404) so callers can treat that as "fresh start".
 *
 * Used by useOnboardingData for VI-551 resume-interview support: callers
 * read `answers.length` to compute resume-from-index and `status` to detect
 * already-Reconciled candidates that should be redirected to the
 * already-completed page.
 */
// Upstream Job Application API wraps the answers response in a `{ data: ... }`
// envelope, which our interview-app backend passes through unchanged. So the
// raw axios response body is `{ data: AnswerDocument }`, not `AnswerDocument`
// directly. We unwrap here so callers see a flat AnswerDocument.
interface AnswerResponseEnvelope {
  data?: AnswerDocument;
}

export const getAnswersByApplicantGuid = async (
  applicantGuid: string,
): Promise<AnswerDocument | null> => {
  try {
    const response = await axios.get<AnswerResponseEnvelope | AnswerDocument>(
      `/api/v1/answer/${applicantGuid}`,
    );
    const body = response.data;
    if (!body) return null;
    // Defensive: support both wrapped `{ data: { ... } }` and flat shapes in
    // case the proxy layer ever changes.
    if ('data' in body && body.data) {
      return body.data;
    }
    if ('applicationGuid' in body) {
      return body as AnswerDocument;
    }
    return null;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    console.error('[VI-594] Failed to fetch answers for applicantGuid', {
      applicantGuid,
      error,
    });
    return null;
  }
};
